import { translate } from 'components/Translation';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import React, { useEffect, useState } from 'react';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import { BodyS, Caption } from 'components/Typography/Typography';
import classNames from 'classnames';
import Add from '@material-ui/icons/Add';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import moment from 'moment';
import { isValidDate } from 'utils/date/validateDate';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import { SingleUser } from 'utils/api/models/users/singleUser';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/departmentDetailsStyle';
import { makeStyles } from '@material-ui/core';
import Remove from '@material-ui/icons/Remove';
import Done from '@material-ui/icons/Done';

// @ts-ignore
const useStyles = makeStyles(styles);

enum TABS {
	USERS = 0,
	PERMISSIONS = 1,
}

interface IAddManagerDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	addManagers: (managers: SingleUser[], expiration: string) => void; // TODO
	availableManagers: SingleUser[];
	loading: boolean;
}

const AddManagerDialog = ({ hideDialog, isOpen, addManagers, availableManagers, loading }: IAddManagerDialogProps) => {
	const [activeTab, setActiveTab] = useState<number>(TABS.USERS);
	const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
	const [unlimitedExpirationDate, setUnlimitedExpirationDate] = useState<boolean>(true);
	const [expirationDate, setExpirationDate] = useState<moment.Moment | null>(null);
	const classes = useStyles();

	const clearAddManagerDialog = () => {
		setActiveTab(TABS.USERS);
		setSelectedManagers([]);
		setUnlimitedExpirationDate(true);
		setExpirationDate(null);
	};

	const setManager = (manager: SingleUser) => {
		const filteredManagers = selectedManagers.filter((id) => id !== manager.userId);
		if (filteredManagers.length < selectedManagers.length) {
			setSelectedManagers(() => filteredManagers);
		} else {
			setSelectedManagers(() => [...selectedManagers, manager.userId]);
		}
	};

	const backCancelClicked = () => {
		if (activeTab === TABS.USERS) {
			hideDialog();
			clearAddManagerDialog();
		} else {
			setActiveTab(TABS.USERS);
		}
	};

	const nextAddClicked = () => {
		if (activeTab === TABS.USERS) {
			setActiveTab(TABS.PERMISSIONS);
		} else {
			hideDialog();
			const managers = availableManagers.filter(
				(manager: SingleUser) => selectedManagers.indexOf(manager.userId) >= 0
			);
			const expiration = unlimitedExpirationDate ? '' : expirationDate?.format('YYYY-MM-DD');
			addManagers(managers, expiration || '');
		}
	};

	useEffect(() => {
		clearAddManagerDialog();
	}, [availableManagers]);

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={() => {
				hideDialog();
				clearAddManagerDialog();
			}}
			header={translate('add.manager')}
			rightSideButtons={[
				{
					text: activeTab === TABS.USERS ? translate('cancel') : translate('back'),
					onClick: backCancelClicked,
					color: 'tertiary-light',
				},
				{
					text: activeTab === TABS.USERS ? translate('next') : translate('add'),
					onClick: nextAddClicked,
					color: 'primary-light',
					disabled: selectedManagers.length <= 0,
				},
			]}
			dialogBody={
				<div className={classes.addManagerDialogBody}>
					<TabsWithDots
						tabs={[
							{
								name: translate('users'),
								content: (
									<div>
										<div className={classes.managersCaption}>
											<Caption color="secondary-light">
												<>
													{translate('departments.choose.managers')}
													<br />
													{availableManagers.length > 0 &&
														translate('departments.only.managers')}
												</>
											</Caption>
										</div>

										<div className={classes.dialogManagers}>
											{availableManagers.map((manager, index) => (
												<div
													className={classNames({
														[classes.selectedManager]:
															selectedManagers.indexOf(manager.userId) !== -1,
														[classes.dialogManager]: true,
													})}
													key={index}
													onClick={() => setManager(manager)}
												>
													<Caption color="brand">{manager.userName}</Caption>
													<div>
														<Add />
														<Done />
														<Remove />
													</div>
												</div>
											))}
										</div>
									</div>
								),
							},
							{
								name: translate('permissions'),
								content: (
									<div className={classes.expirationDateWrapper}>
										<BodyS color="secondary-light">{translate('select.expiration.date')}</BodyS>
										<div>
											<CustomDatePicker
												timeFormat={false}
												value={expirationDate}
												onChange={(date: moment.Moment | string) => {
													if (moment.isMoment(date)) {
														setExpirationDate(() => date);
													}
												}}
												inputProps={{
													placeholder: translate('department.details.expiration.date'),
												}}
												isValidDate={(currentDate) => isValidDate(currentDate, 'yesterday')}
											/>
											<CustomCheckbox
												label={translate('unlimited')}
												checked={unlimitedExpirationDate}
												onClick={() => setUnlimitedExpirationDate((value: boolean) => !value)}
											/>
										</div>
									</div>
								),
							},
						]}
						activeTab={activeTab}
						setActiveTab={setActiveTab}
					/>
				</div>
			}
		/>
	);
};

export default AddManagerDialog;
